import React, { useState } from 'react';
import { X, Calculator, TrendingUp, Scale, Building, FileText, ExternalLink, Info, AlertTriangle, CheckCircle, Clock, Users, BarChart3, Shield, ChevronDown, ChevronUp, Percent, Calendar } from 'lucide-react';
import LetterGenerator from './LetterGenerator';

const AnalysisModal = ({ 
  isOpen, 
  onClose, 
  tenant, 
  calculationResult, 
  adjustmentType, // 'vpi', 'mietspiegel', 'modernisierung', 'staffel'
  tenantType, // 'private' or 'commercial'
  themeClasses,
  mietspiegelData // Pass market data to modal
}) => {
  if (!isOpen || !calculationResult) return null;

  // State for interactive features
  const [showCalculationDetails, setShowCalculationDetails] = useState(false);
  const [whatIfInflation, setWhatIfInflation] = useState(3.0);
  const [whatIfTurnus, setWhatIfTurnus] = useState(12);
  const [showLetterGenerator, setShowLetterGenerator] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  // Helper function to format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', { 
        year: 'numeric', 
        month: 'short' 
      });
    } catch (error) {
      return null;
    }
  };

  // Helper function to get current month/year for "Aktueller Index"
  const getCurrentMonthYear = () => {
    // Use VPI data date if available, otherwise current date
    if (calculationResult?.vpi?.currentDate) {
      const vpiDate = new Date(calculationResult.vpi.currentDate);
      return vpiDate.toLocaleDateString('de-DE', { 
        year: 'numeric', 
        month: 'short' 
      });
    }
    const now = new Date();
    return now.toLocaleDateString('de-DE', { 
      year: 'numeric', 
      month: 'short' 
    });
  };

  // Calculate months since lease start with proper date handling and specific error messages
  const calculateMonthsSinceLeaseStart = () => {
    // Use last_lease_adjustment if available, otherwise fall back to lease_start
    const adjustmentDate = tenant?.last_lease_adjustment || tenant?.lease_start;
    
    if (!adjustmentDate) {
      return { error: 'Datum der letzten Anpassung fehlt' };
    }
    
    try {
      const lastAdjustmentDate = new Date(adjustmentDate);
      if (isNaN(lastAdjustmentDate.getTime())) {
        return { error: 'Ungültiges Datum' };
      }
      
      const now = new Date();
      const monthsDiff = (now.getFullYear() - lastAdjustmentDate.getFullYear()) * 12 + 
                        (now.getMonth() - lastAdjustmentDate.getMonth());
      return { months: monthsDiff };
    } catch (error) {
      console.error('Error calculating months since last adjustment:', error);
      return { error: 'Fehler bei der Datumsberechnung' };
    }
  };

  // Calculate months since lease start - MOVED TO COMPONENT LEVEL FOR PROPER SCOPE
  const monthsResult = calculateMonthsSinceLeaseStart();

  // Extract data from calculationResult structure with better fallbacks
  const tenantData = calculationResult?.tenant || tenant;
  const unitData = calculationResult?.unit;
  const propertyData = calculationResult?.property;

  // Better unit identifier with fallback
  const getUnitIdentifier = () => {
    return unitData?.unit_number || unitData?.identifier || unitData?.name || 'Einheit nicht spezifiziert';
  };

  // Calculate what-if scenarios
  const calculateWhatIf = () => {
    const currentRent = calculationResult.analysis?.mietspiegel?.current?.total || 2594.11;
    const whatIfIncrease = (whatIfInflation / 100) * (whatIfTurnus / 12);
    const whatIfAmount = currentRent * whatIfIncrease;
    const whatIfNewRent = currentRent + whatIfAmount;
    
    return {
      increase: whatIfIncrease,
      amount: whatIfAmount,
      newRent: whatIfNewRent
    };
  };

  const whatIfResult = calculateWhatIf();

  // Determine if legal limits apply (only for residential rent)
  const showLegalLimits = tenantType === 'private' && (adjustmentType === 'mietspiegel' || adjustmentType === 'modernisierung');

  // Context-sensitive content configuration
  const getContextConfig = () => {
    const baseDateFormatted = formatDateForDisplay(tenantData?.base_rent_date || tenantData?.lease_start);
    const currentMonthYear = getCurrentMonthYear();
    
    switch (adjustmentType) {
      case 'vpi':
        // Extract threshold data
        const thresholdPercentage = tenantData?.threshold_percentage || 0;
        const currentIncrease = (calculationResult.analysis?.vpi?.increasePercent || 15);
        const thresholdMet = thresholdPercentage === 0 || currentIncrease >= thresholdPercentage;
        
        // Calculate "Erstmalig erreicht" (First time threshold was reached)
        const calculateFirstReached = () => {
          if (thresholdPercentage === 0) {
            return { value: 'Keine Schwelle definiert', status: null };
          }
          
          const historicalData = calculationResult.vpi?.historicalData || [];
          if (historicalData.length === 0) {
            return { value: 'Daten nicht verfügbar', status: 'Schätzung' };
          }
          
          const baseIndex = parseFloat(calculationResult.vpi?.baseIndex);
          if (!baseIndex || isNaN(baseIndex)) {
            return { value: 'Basis-Index fehlt', status: null };
          }
          
          const leaseStart = new Date(tenantData?.lease_start);
          if (isNaN(leaseStart.getTime())) {
            return { value: 'Vertragsdatum fehlt', status: null };
          }
          
          // Hard limit: 01.01.2023 (business requirement)
          const HARD_LIMIT_DATE = new Date('2023-01-01');
          
          // Effective start date = MAX(lease start, hard limit)
          const effectiveStart = new Date(Math.max(leaseStart.getTime(), HARD_LIMIT_DATE.getTime()));
          
          // Sort chronologically (oldest first)
          const sortedData = [...historicalData].sort((a, b) => a.period.localeCompare(b.period));
          
          // Find first month where threshold was reached
          for (const entry of sortedData) {
            const entryDate = new Date(entry.period + '-01');
            
            // Only check months after effective start date (lease start or 2023-01-01, whichever is later)
            if (entryDate < effectiveStart) continue;
            
            // Calculate increase percentage
            const increase = ((entry.index_value - baseIndex) / baseIndex) * 100;
            
            // Check if threshold is reached
            if (increase >= thresholdPercentage) {
              return { 
                value: formatDateForDisplay(entry.period), 
                status: 'Live (Destatis)',
                statusColor: 'text-green-600'
              };
            }
          }
          
          // Threshold never reached
          return { value: 'Noch nicht erreicht', status: null };
        };
        
        const firstReached = calculateFirstReached();
        
        return {
          title: 'VPI-Indexierung Analyse',
          icon: TrendingUp,
          color: 'purple',
          mainContent: {
            title: 'VPI-Analyse',
            data: [
              { 
                label: `Basis-Index${baseDateFormatted ? ` (${baseDateFormatted})` : ''}`, 
                value: calculationResult.vpi?.baseIndex || '110.5' 
              },
              { 
                label: `Aktueller Index (${currentMonthYear})`, 
                value: calculationResult.vpi?.currentIndex || calculationResult.analysis?.vpi?.currentIndex || '122.3',
                status: 'Live (Destatis)' // Always show as live data (from Genesis API via JSON/Supabase)
              },
              { label: 'Steigerung', value: formatPercentage((calculationResult.analysis?.vpi?.increasePercent || 15) / 100) },
              { 
                label: 'Vertragliche Schwelle', 
                value: thresholdPercentage > 0 ? `${thresholdPercentage}%` : 'Keine Schwelle',
                status: thresholdMet ? '✓ Erreicht' : '⚠ Nicht erreicht',
                statusColor: thresholdMet ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'
              },
              {
                label: 'Erstmalig erreicht',
                value: firstReached.value,
                status: firstReached.status,
                statusColor: firstReached.statusColor
              },
              { label: 'Erhöhungsbetrag', value: formatCurrency(calculationResult.analysis?.vpi?.increaseAmount || 389.12), highlight: true }
            ]
          },
          showWhatIf: true,
          showRetroactive: true // NEW: Enable retroactive calculation
        };
      case 'mietspiegel':
        return {
          title: 'Mietspiegel-Analyse',
          icon: Scale,
          color: 'blue',
          mainContent: {
            title: 'Mietspiegel-Vergleich',
            data: [
              { label: 'Aktuelle Miete/m²', value: `${calculationResult.analysis?.mietspiegel?.current?.perSqm?.toFixed(2) || '18.50'} €/m²` },
              { label: 'Mietspiegel-Mittelwert', value: `${calculationResult.analysis?.mietspiegel?.mietspiegel?.medium?.toFixed(2) || '22.80'} €/m²` },
              { label: 'Mögliche Steigerung', value: formatPercentage((calculationResult.analysis?.mietspiegel?.potential?.toMedium || 456.78) / (calculationResult.analysis?.mietspiegel?.current?.total || 2594.11)) },
              { label: 'Erhöhungsbetrag', value: formatCurrency(calculationResult.analysis?.mietspiegel?.potential?.toMedium || 456.78), highlight: true }
            ]
          },
          showWhatIf: false // No inflation analysis for Mietspiegel
        };
      case 'modernisierung':
        return {
          title: 'Modernisierungs-Analyse',
          icon: Building,
          color: 'green',
          mainContent: {
            title: 'Modernisierungsumlage',
            data: [
              { label: 'Investitionskosten', value: formatCurrency(calculationResult.modernisierung?.totalCosts || 45000) },
              { label: 'Umlagefähig (8%)', value: formatCurrency(calculationResult.modernisierung?.annualAmount || 3600) },
              { label: 'Monatliche Umlage', value: formatCurrency(calculationResult.modernisierung?.monthlyAmount || 300), highlight: true },
              { label: 'Energieeinsparung', value: `${calculationResult.modernisierung?.energySavings || '15'}%` }
            ]
          },
          showWhatIf: false
        };
      case 'staffel':
        return {
          title: 'Staffelmiete-Analyse',
          icon: Calculator,
          color: 'yellow',
          mainContent: {
            title: 'Staffelmiete-Berechnung',
            data: [
              { label: 'Aktuelle Miete', value: formatCurrency(calculationResult.analysis?.mietspiegel?.current?.total || 2594.11) },
              { label: 'Staffel-Erhöhung', value: formatPercentage(calculationResult.staffel?.percent / 100 || 0.03) },
              { label: 'Erhöhungsbetrag', value: formatCurrency(calculationResult.staffel?.amount || 77.82), highlight: true },
              { label: 'Nächste Anpassung', value: calculationResult.staffel?.nextAdjustment || '01.01.2026' }
            ]
          },
          showWhatIf: false
        };
      default:
        return null;
    }
  };

  const config = getContextConfig();
  if (!config) return null;

  const IconComponent = config.icon;
  const colorClasses = {
    purple: 'bg-purple-50 border-purple-200 text-purple-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    green: 'bg-green-50 border-green-200 text-green-800',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800'
  };

  // Handle "Umsetzung vorbereiten" button click
  const handlePrepareImplementation = () => {
    setShowLetterGenerator(true);
  };

  // Handle insight button clicks
  const handleLegalCheck = () => {
    alert(`Ihre Anfrage wird an unsere Partnerkanzlei Müller & Partner weitergeleitet.\n\nSie erhalten in Kürze ein unverbindliches Angebot per E-Mail.\n\n(Platzhalter-Funktion)`);
  };

  const handleSetReminder = () => {
    const reminderText = adjustmentType === 'vpi' 
      ? 'Erinnerung für die nächste VPI-Anpassung im August 2026 wurde eingerichtet.'
      : 'Erinnerung für die nächste Mietspiegelprüfung wurde eingerichtet.';
    alert(`${reminderText}\n\nSie werden per E-Mail benachrichtigt.\n\n(Platzhalter-Funktion)`);
  };

  const handleMarketAnalysis = () => {
    if (!mietspiegelData) {
      alert('Marktdaten sind derzeit nicht verfügbar.');
      return;
    }
    
    const city = propertyData?.city || 'der Region';
    alert(`Detaillierte Marktanalyse für ${city}:\n\n• Einfacher Mietspiegel: ${mietspiegelData.simple?.toFixed(2) || 'N/A'} €/m²\n• Mittlerer Mietspiegel: ${mietspiegelData.medium?.toFixed(2) || 'N/A'} €/m²\n• Guter Mietspiegel: ${mietspiegelData.good?.toFixed(2) || 'N/A'} €/m²\n• Max. Erhöhung (Kappungsgrenze): ${mietspiegelData.maxIncrease || 'N/A'}%`);
  };

  const handleCappingLimit = () => {
    alert(`Kappungsgrenze-Analyse:\n\n• Maximale Erhöhung in 3 Jahren: 20%\n• Ihre geplante Erhöhung: ${((calculationResult.analysis?.mietspiegel?.potential?.toMedium || 456.78) / (calculationResult.analysis?.mietspiegel?.current?.total || 2594.11) * 100).toFixed(1)}%\n• Status: Innerhalb der gesetzlichen Grenzen\n\n(Detaillierte Berechnung verfügbar)`);
  };

  // Calculate market benchmark with real data
  const getMarketBenchmark = () => {
    if (!mietspiegelData || !unitData?.size_sqm) return null;
    
    const currentRent = calculationResult.analysis?.mietspiegel?.current?.total || 2594.11;
    const currentPerSqm = currentRent / unitData.size_sqm;
    const marketAverage = mietspiegelData.medium || 22.80;
    const difference = ((marketAverage - currentPerSqm) / currentPerSqm * 100);
    
    return {
      currentPerSqm,
      marketAverage,
      difference,
      status: difference > 10 ? 'Erhebliches Potenzial' : difference > 0 ? 'Potenzial vorhanden' : 'Marktkonform'
    };
  };

  const marketBenchmark = getMarketBenchmark();

  // Insights data for right column - CONTEXT SENSITIVE
  const getInsights = () => {
    const baseInsights = [
      {
        icon: Shield,
        title: 'Rechtliche Prüfung',
        description: 'Unsere Partnerkanzlei Müller & Partner prüft Ihre Mieterhöhung auf alle rechtlichen Fallstricke.',
        value: 'ab 99 €',
        action: 'Jetzt anfragen',
        color: 'blue',
        onClick: handleLegalCheck
      }
    ];

    // Context-specific insights
    if (adjustmentType === 'vpi') {
      baseInsights.push({
        icon: Clock,
        title: 'Nächstes Potenzial',
        description: 'Basierend auf der aktuellen Inflationsprognose ist die nächste VPI-Anpassung voraussichtlich im August 2026 möglich.',
        value: 'Aug 2026',
        action: 'Erinnerung einrichten',
        color: 'purple',
        onClick: handleSetReminder
      });
    } else if (adjustmentType === 'mietspiegel') {
      baseInsights.push({
        icon: Percent,
        title: 'Kappungsgrenze',
        description: 'Prüfung der gesetzlichen Kappungsgrenze (max. 20% in 3 Jahren) für Ihre geplante Mieterhöhung.',
        value: 'Konform',
        action: 'Details anzeigen',
        color: 'green',
        onClick: handleCappingLimit
      });
    }

    // Market benchmark (always relevant)
    baseInsights.push({
      icon: BarChart3,
      title: 'Objekt-Benchmark',
      description: marketBenchmark ? 
        `Ihre aktuelle Miete von ${marketBenchmark.currentPerSqm.toFixed(2)} €/m² liegt ${Math.abs(marketBenchmark.difference).toFixed(0)}% ${marketBenchmark.difference > 0 ? 'unter' : 'über'} dem Marktdurchschnitt von ${marketBenchmark.marketAverage.toFixed(2)} €/m².` :
        'Vergleich mit regionalen Durchschnittsmieten.',
      value: marketBenchmark?.status || 'Daten laden...',
      action: 'Detaillierte Marktanalyse',
      color: marketBenchmark?.difference > 10 ? 'green' : 'gray',
      onClick: handleMarketAnalysis
    });

    return baseInsights;
  };

  const insights = getInsights();

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`${themeClasses.cardBg} rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto`}>
          {/* Content */}
          <div className="p-6">
            {/* Header - MASTER LIVE STYLE WITH DEBUG */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className={`text-xl font-semibold ${themeClasses.text} flex items-center`}>
                  <IconComponent className={`w-6 h-6 mr-3 text-${config.color}-600`} />
                  {config.title} - {tenantData?.first_name || ''} {tenantData?.last_name || tenantData?.name || 'Mieter'} ({propertyData?.property_name || propertyData?.name || 'Objekt'} • {getUnitIdentifier()})
                </h3>
                <p className="text-xs text-gray-500 mt-1">DEBUG: src/components/AnalysisModal_Master_v1.2.jsx | Build: 2025-08-23 16:45:00</p>
                <div className="flex items-center mt-2 space-x-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    <div className="w-2 h-2 bg-green-400 rounded-full mr-1.5 animate-pulse"></div>
                    Master Live v1.2
                  </span>
                  <span className={`text-sm ${themeClasses.textSecondary}`}>
                    {new Date().toLocaleString('de-DE', { 
                      day: '2-digit', 
                      month: '2-digit', 
                      year: 'numeric', 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className={`${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tenant Info - ENHANCED WITH BETTER ERROR HANDLING */}
            <div className={`p-4 rounded-lg border ${colorClasses[config.color]} mb-6`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h4 className="font-medium text-sm opacity-75">Mieter</h4>
                  <p className="font-semibold">{tenantData?.first_name || ''} {tenantData?.last_name || tenantData?.name || 'Mieter'}</p>
                  <p className="text-sm opacity-75">Mietvertrag seit: {formatDateForDisplay(tenantData?.lease_start) || 'Unbekannt'}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm opacity-75">Objekt & Einheit</h4>
                  <p className="font-semibold">{propertyData?.property_name || propertyData?.name || 'Objekt'}</p>
                  <p className="text-sm opacity-75">Einheit {getUnitIdentifier()} • {unitData?.size_sqm || 'Unbekannt'} m²</p>
                </div>
                <div className="text-right">
                  <h4 className="font-medium text-sm opacity-75">Letzte Anpassung</h4>
                  {monthsResult.months !== undefined ? (
                    <>
                      <p className="font-semibold">{monthsResult.months} Monate</p>
                      <p className="text-sm opacity-75">{(monthsResult.months / 12).toFixed(1)} Jahre</p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold text-red-600 text-sm">Berechnung fehlgeschlagen</p>
                      <p className="text-xs text-red-500">{monthsResult.error}</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Main Content - 2/3 - 1/3 LAYOUT */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Left Side - Main Analysis (2/3) */}
              <div className="lg:col-span-2 space-y-6">
                {/* Key Metrics */}
                <div className={`p-6 rounded-lg border ${themeClasses.cardBorder}`}>
                  <h4 className={`font-medium mb-4 ${themeClasses.textPrimary}`}>
                    {config.mainContent.title}
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {config.mainContent.data.map((item, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <span className={`${themeClasses.textSecondary} text-sm`}>
                          {item.label}
                          {item.status && (
                            <span className={`ml-2 text-xs px-2 py-1 rounded font-medium ${
                              item.statusColor || 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            }`}>
                              {item.status}
                            </span>
                          )}
                        </span>
                        <span className={`font-medium ${item.highlight ? 'text-green-600 dark:text-green-400 text-lg' : themeClasses.textPrimary}`}>
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Calculation Details Accordion */}
                  <div className="mt-6">
                    <button
                      onClick={() => setShowCalculationDetails(!showCalculationDetails)}
                      className="flex items-center justify-between w-full p-3 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <span className="font-medium text-gray-700 dark:text-gray-300">Berechnungsdetails anzeigen</span>
                      {showCalculationDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    
                    {showCalculationDetails && (
                      <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="space-y-2 text-sm font-mono text-gray-600 dark:text-gray-300">
                          {adjustmentType === 'vpi' ? (
                            <>
                              <div>Basis-Index: {calculationResult.vpi?.baseIndex || '110.5'}</div>
                              <div>Aktueller Index: {calculationResult.vpi?.currentIndex || '127.2'}</div>
                              <div>Steigerung: ({calculationResult.vpi?.currentIndex || '127.2'} - {calculationResult.vpi?.baseIndex || '110.5'}) / {calculationResult.vpi?.baseIndex || '110.5'} = {formatPercentage((calculationResult.analysis?.vpi?.increasePercent || 15) / 100)}</div>
                              <div>Erhöhungsbetrag: {formatCurrency(calculationResult.analysis?.mietspiegel?.current?.total || 2594.11)} × {formatPercentage((calculationResult.analysis?.vpi?.increasePercent || 15) / 100)} = {formatCurrency(calculationResult.analysis?.vpi?.increaseAmount || 389.12)}</div>
                            </>
                          ) : (
                            <>
                              <div>Aktuelle Miete: {formatCurrency(calculationResult.analysis?.mietspiegel?.current?.total || 2594.11)}</div>
                              <div>Mietspiegel-Mittelwert: {formatCurrency((calculationResult.analysis?.mietspiegel?.mietspiegel?.medium || 22.80) * (unitData?.size_sqm || 87))}</div>
                              <div>Erhöhungspotenzial: {formatCurrency(calculationResult.analysis?.mietspiegel?.potential?.toMedium || 456.78)}</div>
                            </>
                          )}
                          <div>Neue Miete: {formatCurrency((calculationResult.analysis?.mietspiegel?.current?.total || 2594.11) + (calculationResult.analysis?.vpi?.increaseAmount || calculationResult.analysis?.mietspiegel?.potential?.toMedium || 389.12))}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* What-If Analysis - CONDITIONAL FOR VPI ONLY */}
                {config.showWhatIf && (
                  <div className={`p-6 rounded-lg border ${themeClasses.cardBorder} bg-gradient-to-r from-blue-50 dark:from-blue-900/20 to-purple-50 dark:to-purple-900/20`}>
                    <h4 className={`font-medium mb-4 ${themeClasses.textPrimary} flex items-center`}>
                      <Calculator className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
                      Was-wäre-wenn-Analyse
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-6 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Erwartete Inflation (%)
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="8"
                          step="0.1"
                          value={whatIfInflation}
                          onChange={(e) => setWhatIfInflation(parseFloat(e.target.value))}
                          className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="text-center text-sm text-gray-600 dark:text-gray-300 mt-1">{whatIfInflation.toFixed(1)}%</div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Anpassungs-Turnus (Monate)
                        </label>
                        <input
                          type="range"
                          min="6"
                          max="24"
                          step="1"
                          value={whatIfTurnus}
                          onChange={(e) => setWhatIfTurnus(parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="text-center text-sm text-gray-600 dark:text-gray-300 mt-1">{whatIfTurnus} Monate</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700">
                      <div className="text-center">
                        <div className="text-sm text-gray-600 dark:text-gray-300">Steigerung</div>
                        <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">{formatPercentage(whatIfResult.increase)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 dark:text-gray-300">Erhöhungsbetrag</div>
                        <div className="text-lg font-semibold text-green-600 dark:text-green-400">{formatCurrency(whatIfResult.amount)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 dark:text-gray-300">Neue Miete</div>
                        <div className="text-lg font-semibold text-purple-600 dark:text-purple-400">{formatCurrency(whatIfResult.newRent)}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* NEW: Retroactive Calculation (3 Years) - VPI Only */}
                {config.showRetroactive && adjustmentType === 'vpi' && (
                  <div className={`p-6 rounded-lg border ${themeClasses.cardBorder} bg-gradient-to-r from-purple-50 dark:from-purple-900/20 to-blue-50 dark:to-blue-900/20`}>
                    <h4 className={`font-medium mb-4 ${themeClasses.textPrimary} flex items-center`}>
                      <Clock className="w-5 h-5 mr-2 text-purple-600 dark:text-purple-400" />
                      Rückwirkende Berechnung (bis zu 4 Jahre)
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                      Gemäß § 557 BGB (Indexmiete bei Gewerberaum) können VPI-Anpassungen bis zu 3 Jahre rückwirkend geltend gemacht werden, 
                      sofern sie nicht bereits durchgeführt wurden.
                    </p>
                    
                    {(() => {
                      // Define variables needed for Info Box BEFORE rendering
                      const currentRent = calculationResult.tenant?.cold_rent || calculationResult.tenant?.monthly_rent || 0;
                      const thresholdPercentage = calculationResult.tenant?.threshold_percentage || 
                                                  calculationResult.analysis?.vpi?.thresholdPercentage || 3;
                      
                      return (
                        <>
                          {/* Info Box with Base Data */}
                          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <div className="text-gray-600 dark:text-gray-400">Vertragliche Schwelle</div>
                                <div className="font-semibold text-blue-900 dark:text-blue-100">{thresholdPercentage}%</div>
                              </div>
                              <div>
                                <div className="text-gray-600 dark:text-gray-400">Mietvertragsbeginn</div>
                                <div className="font-semibold text-blue-900 dark:text-blue-100">
                                  {formatDateForDisplay(calculationResult.tenant?.lease_start) || 'Nicht angegeben'}
                                </div>
                              </div>
                              <div>
                                <div className="text-gray-600 dark:text-gray-400">Aktuelle Kaltmiete</div>
                                <div className="font-semibold text-blue-900 dark:text-blue-100">{formatCurrency(currentRent)}</div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Continue with calculation logic */}
                          {(() => {
                            const baseIndex = parseFloat(calculationResult.vpi?.baseIndex);
                            const currentIndex = parseFloat(calculationResult.vpi?.currentIndex);
                            
                            // Check if we have real VPI data
                            if (!calculationResult.vpi?.serviceAvailable && !calculationResult.analysis?.vpi?.serviceAvailable) {
                              return (
                                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                                  <div className="flex items-start space-x-2">
                                    <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
                                    <div className="text-sm text-red-800 dark:text-red-200">
                                      <strong>VPI-Daten nicht verfügbar:</strong> Die rückwirkende Berechnung kann nicht durchgeführt werden, 
                                      da keine echten VPI-Daten von Destatis geladen werden konnten. Bitte versuchen Sie es später erneut.
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            
                            // Get real historical VPI data
                            const historicalData = calculationResult.vpi?.historicalData || [];
                            
                            if (!historicalData || historicalData.length === 0) {
                              return (
                                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                                  <div className="flex items-start space-x-2">
                                    <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                                    <div className="text-sm text-yellow-800 dark:text-yellow-200">
                                      <strong>Historische Daten nicht verfügbar:</strong> Die rückwirkende Berechnung kann nicht durchgeführt werden, 
                                      da keine historischen VPI-Daten geladen wurden. Bitte laden Sie die Seite neu.
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            
                            const leaseStartDate = new Date(calculationResult.tenant?.lease_start);
                            const baseDate = new Date(calculationResult.tenant?.base_rent_date || calculationResult.tenant?.lease_start);
                            const currentYear = new Date().getFullYear();
                            
                            // Get the latest available month from Destatis data (e.g., "2025-08")
                            const latestPeriod = calculationResult.vpi?.currentDate || 
                                          (historicalData && historicalData.length > 0 ? historicalData[0].period : '2025-08');
                            const [latestYear, latestMonth] = latestPeriod.split('-');
                            
                            // NEW: Build retroactive calculations with MONTHLY breakdown grouped by YEAR
                            // This provides full transparency: each month shows index, percentage, and amount
                            // Months are grouped into year blocks for better overview
                            const retroYears = [];
                            let totalRetroactive = 0;
                            
                            // Group months by year (from lease start to current month)
                            const leaseStartYear = leaseStartDate.getFullYear();
                            const leaseStartMonth = leaseStartDate.getMonth() + 1; // 1-12
                            const currentYearNum = parseInt(latestYear);
                            const currentMonthNum = parseInt(latestMonth);
                            
                            // Iterate through each year from lease start to current
                            // BUT: Limit to maximum 4 years back (§ 557 BGB: max 3 years retroactive)
                            // Also: Never go back before 2022 (VPI data availability)
                            const earliestYear = Math.max(
                              leaseStartYear,           // Don't go before lease start
                              currentYearNum - 4,       // Max 4 years back
                              2022                      // Never before 2022 (VPI data limit)
                            );
                            
                            for (let year = earliestYear; year <= currentYearNum; year++) {
                              const yearData = {
                                year: year,
                                label: year === currentYearNum ? `Aktuelles Jahr (${year})` : 
                                       currentYearNum - year === 1 ? `Jahr 1 zurück (${year})` :
                                       `Jahr ${currentYearNum - year} zurück (${year})`,
                                months: [],
                                yearTotal: 0,
                                thresholdMetInYear: false
                              };
                              
                              // Determine start and end month for this year
                              const startMonth = (year === leaseStartYear) ? leaseStartMonth : 1;
                              const endMonth = (year === currentYearNum) ? currentMonthNum : 12;
                              
                              // Iterate through each month in this year
                              for (let month = startMonth; month <= endMonth; month++) {
                                const monthStr = month.toString().padStart(2, '0');
                                const period = `${year}-${monthStr}`;
                                
                                // Find VPI data for this month
                                const vpiEntry = historicalData.find(d => d.period === period);
                                
                                if (vpiEntry) {
                                  const monthIndex = vpiEntry.index_value;
                                  
                                  // Calculate percentage increase from base index
                                  const percentageFromBase = ((monthIndex - baseIndex) / baseIndex) * 100;
                                  
                                  // Check if threshold was reached
                                  const thresholdMet = percentageFromBase >= thresholdPercentage;
                                  
                                  let monthlyAmount = 0;
                                  if (thresholdMet) {
                                    // Calculate the rent increase for this month
                                    // Formula: current_rent × (current_index / base_index - 1)
                                    monthlyAmount = currentRent * (monthIndex / baseIndex - 1);
                                    yearData.yearTotal += monthlyAmount;
                                    yearData.thresholdMetInYear = true;
                                  }
                                  
                                  yearData.months.push({
                                    month: month,
                                    monthName: new Date(year, month - 1, 1).toLocaleDateString('de-DE', { month: 'short' }),
                                    period: period,
                                    index: monthIndex,
                                    percentageFromBase: percentageFromBase,
                                    thresholdMet: thresholdMet,
                                    amount: monthlyAmount
                                  });
                                }
                              }
                              
                              totalRetroactive += yearData.yearTotal;
                              retroYears.push(yearData);
                            }
                            
                            if (retroYears.length === 0) {
                              return (
                                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                                  <div className="flex items-start space-x-2">
                                    <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                                    <div className="text-sm text-yellow-800 dark:text-yellow-200">
                                      <strong>Keine rückwirkenden Anpassungen:</strong> Basierend auf den historischen VPI-Daten 
                                      wurden keine Perioden gefunden, in denen die Schwelle ({thresholdPercentage}%) erreicht wurde, 
                                      oder alle geprüften Perioden liegen vor dem Mietvertragsbeginn.
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            
                            return (
                              <>
                                <div className="space-y-4 mb-4">
                                  {retroYears.map((yearBlock, yearIdx) => (
                                    <div key={yearIdx} className={`border rounded-lg overflow-hidden ${
                                      yearBlock.thresholdMetInYear 
                                        ? 'border-purple-300 dark:border-purple-700' 
                                        : 'border-gray-200 dark:border-gray-700'
                                    }`}>
                                      {/* Year Header */}
                                      <div className={`p-3 ${
                                        yearBlock.thresholdMetInYear 
                                          ? 'bg-purple-100 dark:bg-purple-900/30' 
                                          : 'bg-gray-100 dark:bg-gray-800'
                                      }`}>
                                        <div className="flex justify-between items-center">
                                          <div className="font-medium text-gray-900 dark:text-gray-100">
                                            📅 {yearBlock.label}
                                          </div>
                                          <div className={`font-semibold ${
                                            yearBlock.thresholdMetInYear 
                                              ? 'text-purple-600 dark:text-purple-400' 
                                              : 'text-gray-400 dark:text-gray-600'
                                          }`}>
                                            📊 {formatCurrency(yearBlock.yearTotal)}
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* Monthly Details */}
                                      <div className="p-3 space-y-2">
                                        {yearBlock.months.map((monthData, monthIdx) => (
                                          <div key={monthIdx} className={`flex justify-between items-center p-2 rounded text-sm ${
                                            monthData.thresholdMet 
                                              ? 'bg-green-50 dark:bg-green-900/20' 
                                              : 'bg-gray-50 dark:bg-gray-800'
                                          }`}>
                                            <div className="flex items-center gap-2">
                                              {monthData.thresholdMet && <span className="text-green-600 dark:text-green-400">✅</span>}
                                              <span className="font-mono text-gray-700 dark:text-gray-300">
                                                {monthData.monthName} {yearBlock.year}:
                                              </span>
                                              <span className="text-gray-600 dark:text-gray-400">
                                                Index {monthData.index.toFixed(1)} → {monthData.percentageFromBase.toFixed(2)}%
                                              </span>
                                              {!monthData.thresholdMet && (
                                                <span className="text-xs text-gray-500 dark:text-gray-500">
                                                  (Schwelle nicht erreicht)
                                                </span>
                                              )}
                                            </div>
                                            <div className={`font-semibold ${
                                              monthData.thresholdMet 
                                                ? 'text-green-600 dark:text-green-400' 
                                                : 'text-gray-400 dark:text-gray-600'
                                            }`}>
                                              {formatCurrency(monthData.amount)}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                
                                {/* Total Summary */}
                                <div className="p-4 bg-purple-100 dark:bg-purple-900/30 rounded-lg border-2 border-purple-300 dark:border-purple-700">
                                  <div className="flex justify-between items-center mb-3">
                                    <div>
                                      <div className="font-medium text-purple-900 dark:text-purple-100">
                                        💰 Gesamtforderung (rückwirkend)
                                      </div>
                                      <div className="text-sm text-purple-700 dark:text-purple-300">
                                        {retroYears.filter(y => y.thresholdMetInYear).length} von {retroYears.length} Jahren mit Schwellenerreichung
                                      </div>
                                    </div>
                                    <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                                      {formatCurrency(totalRetroactive)}
                                    </div>
                                  </div>
                                  <div className="text-xs text-purple-700 dark:text-purple-300 border-t border-purple-300 dark:border-purple-700 pt-2">
                                    Berechnungsbasis: {thresholdPercentage}% Schwelle • Referenzmonat: {latestMonth}/{latestYear}
                                  </div>
                                </div>
                                
                                <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                                  <div className="flex items-start space-x-2">
                                    <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                                    <div className="text-sm text-yellow-800 dark:text-yellow-200">
                                      <strong>Rechtlicher Hinweis:</strong> Diese Berechnung ist eine Schätzung. 
                                      Die tatsächliche rückwirkende Forderung hängt von den historischen VPI-Werten 
                                      und dem Zeitpunkt der letzten Anpassung ab. Bitte konsultieren Sie einen Fachanwalt.
                                    </div>
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Alternative content for non-VPI cases */}
                {!config.showWhatIf && adjustmentType === 'mietspiegel' && (
                  <div className={`p-6 rounded-lg border ${themeClasses.cardBorder} bg-gradient-to-r from-blue-50 to-green-50`}>
                    <h4 className={`font-medium mb-4 ${themeClasses.textPrimary} flex items-center`}>
                      <Scale className="w-5 h-5 mr-2 text-blue-600" />
                      Rechtliche Hinweise
                    </h4>
                    <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                      <div className="flex items-start space-x-3">
                        <Calendar className="w-4 h-4 text-blue-600 mt-0.5" />
                        <div>
                          <div className="font-medium">Ankündigungsfrist</div>
                          <div>Mindestens 3 Monate vor Mieterhöhung</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Percent className="w-4 h-4 text-green-600 mt-0.5" />
                        <div>
                          <div className="font-medium">Kappungsgrenze</div>
                          <div>Maximal 20% Erhöhung in 3 Jahren</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <FileText className="w-4 h-4 text-purple-600 mt-0.5" />
                        <div>
                          <div className="font-medium">Begründungspflicht</div>
                          <div>Verweis auf qualifizierten Mietspiegel erforderlich</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Side - Live Insights (1/3) */}
              <div className="space-y-4">
                <div className="flex items-center mb-4">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                  <h4 className={`font-medium ${themeClasses.textPrimary}`}>Live Insights</h4>
                </div>
                
                {insights.map((insight, index) => (
                  <div key={index} className={`p-4 rounded-lg border border-${insight.color}-200 dark:border-${insight.color}-700 bg-${insight.color}-50 dark:bg-${insight.color}-900/20 hover:shadow-md transition-shadow`}>
                    <div className="flex items-start space-x-3">
                      <insight.icon className={`h-5 w-5 text-${insight.color}-600 dark:text-${insight.color}-400 mt-0.5 flex-shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <h5 className={`font-medium text-${insight.color}-800 dark:text-${insight.color}-200 text-sm mb-2`}>
                          {insight.title}
                        </h5>
                        <p className={`text-xs text-${insight.color}-700 dark:text-${insight.color}-300 leading-relaxed mb-3`}>
                          {insight.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className={`text-xs font-medium text-${insight.color}-800 dark:text-${insight.color}-200`}>
                            {insight.value}
                          </span>
                          <button 
                            onClick={insight.onClick}
                            className={`text-xs px-3 py-1 bg-${insight.color}-600 dark:bg-${insight.color}-700 text-white rounded hover:bg-${insight.color}-700 dark:hover:bg-${insight.color}-600 transition-colors`}
                          >
                            {insight.action}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Legal Limits (for residential only) */}
            {showLegalLimits && (
              <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 mb-6">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800 mb-2">
                      Rechtliche Beschränkungen
                    </h4>
                    <div className="text-sm text-amber-700 space-y-1">
                      <p>• Kappungsgrenze: Max. 20% Erhöhung in 3 Jahren</p>
                      <p>• Mietpreisbremse: Beachten Sie örtliche Regelungen</p>
                      <p>• Ankündigungsfrist: 3 Monate vor Mieterhöhung</p>
                      <p>• Begründungspflicht: Mietspiegel oder Vergleichsmieten erforderlich</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recommendation */}
            <div className="p-4 rounded-lg border border-green-200 bg-green-50 mb-6">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-green-800 mb-2">
                    Empfehlung
                  </h4>
                  <p className="text-sm text-green-700">
                    {adjustmentType === 'vpi' && 'VPI-Indexierung ist rechtlich am sichersten und einfachsten durchsetzbar.'}
                    {adjustmentType === 'mietspiegel' && 'Mietspiegelerhöhung bietet gutes Potenzial bei moderatem Aufwand.'}
                    {adjustmentType === 'modernisierung' && 'Modernisierungsumlage ist langfristig vorteilhaft und steigert den Immobilienwert.'}
                    {adjustmentType === 'staffel' && 'Staffelmiete bietet Planungssicherheit für beide Parteien.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={onClose}
                className={`px-4 py-2 rounded-lg ${themeClasses.buttonSecondary} hover:bg-gray-100 transition-colors`}
              >
                Schließen
              </button>
              <button
                onClick={handlePrepareImplementation}
                className={`px-4 py-2 rounded-lg bg-${config.color}-600 text-white hover:bg-${config.color}-700 transition-colors flex items-center`}
              >
                <FileText className="w-4 h-4 mr-2" />
                Mieterhöhungsschreiben erstellen
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Letter Generator Modal */}
      <LetterGenerator
        isOpen={showLetterGenerator}
        onClose={() => setShowLetterGenerator(false)}
        tenantData={tenantData}
        unitData={unitData}
        propertyData={propertyData}
        calculationResult={calculationResult}
        adjustmentType={adjustmentType}
        themeClasses={themeClasses}
      />
    </>
  );
};

export default AnalysisModal;

